import { createApp } from 'vue';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import DialogService from 'primevue/dialogservice';
import PerfectScrollbar from 'vue3-perfect-scrollbar';
import ConfirmationService from 'primevue/confirmationservice';
import { createGtm } from '@gtm-support/vue-gtm';
import axios, { type AxiosRequestConfig } from 'axios';
import App from './App.vue';
import './registerServiceWorker';
import router from './router';

import AppPageLayout from './layouts/AppPageLayout.vue';

import InputText from './components/Form/InputText.vue';
import InputTextarea from './components/Form/Textarea.vue';
import InputNumber from './components/Form/InputNumber.vue';
import InputMask from './components/Form/InputMask.vue';
import InputCheckbox from './components/Form/Checkbox.vue';
import InputRadio from './components/Form/Radio.vue';
import InputPassword from './components/Form/Password.vue';
import InputDropdown from './components/Form/Dropdown.vue';

import UiButton from './components/Ui/Button.vue';
import UiCard from './components/Ui/Card.vue';
import AppIcon from './components/Ui/AppIcon.vue';
import UiMessage from './components/Ui/Message.vue';

import FormBody from './components/Form/Body.vue';

axios.defaults.baseURL = 'https://appewidencjaenergii.pl/api/';
if (localStorage.getItem('auth._token') !== undefined && localStorage.getItem('auth._token') !== null) {
	// Do not replace Axios' default headers here. Replacing the whole object removes
	// Accept: application/json, making an expired token look like a web request.
	axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem('auth._token')}`;
}
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.headers.common['Access-Control-Allow-Origin'] = 'https://appewidencjaenergii.pl/api/';
axios.defaults.headers.common['Access-Control-Allow-Credentials'] = true;
axios.defaults.headers.common['Access-Control-Allow-Headers'] = 'Content-Type,Authorization';
axios.defaults.headers.common['Access-Control-Allow-Methods'] = 'GET,PUT,POST,DELETE,OPTIONS';

type LoadingRequestConfig = AxiosRequestConfig & {
	skipLoadingIndicator?: boolean;
};

let activeLoadingRequests = 0;

const usesLoadingIndicator = (config?: LoadingRequestConfig) => !config?.skipLoadingIndicator;

const startLoadingIndicator = () => {
	activeLoadingRequests += 1;
	document.body.classList.add('loading-indicator');
};

const stopLoadingIndicator = () => {
	activeLoadingRequests = Math.max(0, activeLoadingRequests - 1);
	if (activeLoadingRequests === 0) {
		document.body.classList.remove('loading-indicator');
	}
};

axios.interceptors.request.use((config) => {
	if (usesLoadingIndicator(config)) {
		startLoadingIndicator();
	}
	return config;
}, (error) => Promise.reject(error));

axios.interceptors.response.use((response) => {
	if (usesLoadingIndicator(response.config)) {
		stopLoadingIndicator();
	}
	return response;
}, (error) => {
	console.log(error);
	if (usesLoadingIndicator(error.config)) {
		stopLoadingIndicator();
	}
	return Promise.reject(error);
});

const app = createApp(App);
const pinia = createPinia();
app.use(router);
app.use(PrimeVue);
app.use(ToastService);
app.use(DialogService);
app.use(ConfirmationService);
app.use(PerfectScrollbar);
app.use(pinia);

app.use(
	createGtm({
		id: 'GTM-PK3DH84',
		vueRouter: router,
	}),
)

app.component('InputText', InputText);
app.component('InputTextarea', InputTextarea);
app.component('InputNumber', InputNumber);
app.component('InputMask', InputMask);
app.component('InputCheckbox', InputCheckbox);
app.component('InputRadio', InputRadio);
app.component('InputPassword', InputPassword);
app.component('InputDropdown', InputDropdown);
app.component('UiButton', UiButton);
app.component('UiCard', UiCard);
app.component('AppIcon', AppIcon);
app.component('UiMessage', UiMessage);
app.component('FormBody', FormBody);
app.component('AppPageLayout', AppPageLayout);

app.directive('tooltip', Tooltip);

app.mount('#app');
